import { Check, LoaderCircle } from "lucide-react";
import {
  ALLOWED_TON_POOL_DENOMINATIONS,
  CREATE_POOL_FEES,
  formatUnits,
  type CreatePoolState,
} from "@zkresistor/shared";
import { useCreatePoolForm, type ClientRuntime } from "@zkresistor/client";
import { JettonIdentity } from "../components/Pool";

interface CreatePoolPageProps {
  state: CreatePoolState;
  controller: ClientRuntime["createPool"];
}

export function CreatePoolPage({ state, controller }: CreatePoolPageProps) {
  const form = useCreatePoolForm(controller);

  return (
    <section className="flow">
      <div className="page-head"><h1>Create Pool</h1></div>
      <div className="segmented">
        <button type="button" className={form.kind === "ton" ? "active" : ""} onClick={() => form.setKind("ton")}>GRAM</button>
        <button type="button" className={form.kind === "jetton" ? "active" : ""} onClick={() => form.setKind("jetton")}>Jetton</button>
      </div>

      {form.kind === "ton" ? (
        <div className="form-stack">
          <label>
            Denomination
            <select value={form.tonDenomination} onChange={(event) => form.setTonDenomination(event.target.value)}>
              {ALLOWED_TON_POOL_DENOMINATIONS.map((value) => (
                <option key={value.toString()} value={value.toString()}>{formatUnits(value, 9)} GRAM</option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <div className="form-stack">
          <label>
            Jetton master
            <input
              value={form.master}
              onChange={(event) => form.updateMaster(event.target.value)}
              onBlur={() => void form.resolveMaster()}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          {form.resolving && <p className="loading"><LoaderCircle size={18} />Reading token metadata</p>}
          {form.resolutionError !== null && <p className="alert error">{form.resolutionError}</p>}
          {form.resolved !== null && (
            <>
              <JettonIdentity metadata={form.resolved.metadata} />
              <label>
                Denomination ({form.resolved.metadata.symbol})
                <input
                  inputMode="decimal"
                  placeholder="1000"
                  value={form.denomination}
                  onChange={(event) => form.setDenomination(event.target.value)}
                />
              </label>
            </>
          )}
        </div>
      )}

      <div className="amount-band">
        <span>Factory message</span>
        <strong>{formatUnits(CREATE_POOL_FEES.defaultValue, 9)} GRAM</strong>
      </div>
      {state.status === "error" && <p className="alert error">{state.error}</p>}
      {state.status === "submitted" && <p className="alert success"><Check size={17} />Creation transaction submitted.</p>}
      <button
        className="primary"
        type="button"
        disabled={state.status === "working" || (form.kind === "jetton" && (form.resolved === null || !form.validDenomination))}
        onClick={form.submit}
      >
        {state.status === "working"
          ? <><LoaderCircle size={18} />{state.phase === "resolving-token" ? "Verifying token" : "Waiting for wallet"}</>
          : "Create Pool"}
      </button>
    </section>
  );
}
